/**
 * What kind of time an entry is, and what that means for money.
 *
 * Four kinds, from two stored switches and the project's billing type:
 *
 *   project billing   billable on        billable off
 *   ---------------   ----------------   ------------
 *   hourly / none     Billable  (hourly) No charge
 *   fixed / retainer  Project work       Project work (the switch is ignored)
 *   internal          Internal           Internal     (your own projects)
 *   any, internal on  Internal — admin and overhead, never charged
 *
 * Only Billable time carries money: hours × the rate snapshotted when it was
 * logged, and it is the only kind that waits on an invoice. Project work is
 * already paid for by the fee or retainer; it counts against the project's
 * hour budget instead. No charge is client work you chose not to bill.
 *
 * Nothing about existing entries had to change: `billable` keeps its meaning,
 * `internal` is a new switch that starts off everywhere, and the project's
 * billing type is read at report time. Two consequences worth knowing:
 *   - Time logged before Internal existed with no project, no client and
 *     billable off was ad-hoc admin in all but name, so it reads as Internal.
 *   - Change a project's billing type and its history is read the new way,
 *     which is what you want when the type was set wrong in the first place.
 *
 * Pure: imports types only, so tests/billing.test.mjs can load it directly.
 */

import type { Project, TimeEntry } from "./types";

export type EntryKind = "hourly" | "no_charge" | "project" | "internal";

export const KIND_LABEL: Record<EntryKind, string> = {
  hourly: "Billable",
  no_charge: "No charge",
  project: "Project work",
  internal: "Internal",
};

/** One line on what each kind does, for hints and titles. */
export const KIND_HINT: Record<EntryKind, string> = {
  hourly: "Charged at the rate, and waits on an invoice.",
  no_charge: "Client work you're not billing.",
  project: "Covered by the fixed fee or retainer; counts against the project's hours.",
  internal: "Admin and overhead: never client work, never charged.",
};

/** A project of your own — no client — where every hour is Internal. */
export function isInternalProject(project?: Pick<Project, "billingType"> | null): boolean {
  return project?.billingType === "internal";
}

/**
 * The project's own hourly rate, or null to use the client's. PocketBase saves
 * a blank number as 0, so 0 means "not set" here; a project billed at nothing
 * logs its time as No charge instead.
 */
export function projectRate(project?: Pick<Project, "defaultRate"> | null): number | null {
  return project?.defaultRate && project.defaultRate > 0 ? project.defaultRate : null;
}

/**
 * What a project still needs before its money adds up, or null if nothing.
 * Hourly work needs a rate (its own or the client's); a fee or retainer needs
 * its fee amount. Internal work needs neither. A project with no billing type
 * counts as hourly, but if it has no rate either, the likelier gap is that its
 * type was never chosen, so that is what it asks for.
 */
export function missingTerms(
  project: Pick<Project, "billingType" | "defaultRate" | "feeAmount">,
  clientRate: number | null | undefined,
): "billing" | "rate" | "fee" | null {
  if (isInternalProject(project)) return null;
  if (isFlatFee(project)) return project.feeAmount && project.feeAmount > 0 ? null : "fee";
  if (projectRate(project) ?? (clientRate && clientRate > 0 ? clientRate : null)) return null;
  return !project.billingType || project.billingType === "none" ? "billing" : "rate";
}

export const MISSING_TERMS_LABEL: Record<"billing" | "rate" | "fee", string> = {
  billing: "billing not set", rate: "no rate", fee: "fee not set",
};

/** Fixed-fee and retainer projects are paid for up front, not by the hour. */
export function isFlatFee(project?: Pick<Project, "billingType"> | null): boolean {
  return project?.billingType === "fixed" || project?.billingType === "retainer";
}

type EntryShape = Pick<TimeEntry, "billable" | "internal" | "project" | "client">;

export function kindOf(e: EntryShape, project?: Pick<Project, "billingType"> | null): EntryKind {
  if (e.internal || isInternalProject(project)) return "internal";
  if (!e.project && !e.client && !e.billable) return "internal";
  if (isFlatFee(project)) return "project";
  return e.billable ? "hourly" : "no_charge";
}

/** Look the entry's project up, then classify it. */
export function kindIn(e: EntryShape, projects: Pick<Project, "id" | "billingType">[]): EntryKind {
  return kindOf(e, e.project ? projects.find((p) => p.id === e.project) : undefined);
}

/**
 * The choices offered when logging, given where the time is going. A flat-fee
 * project never offers Billable / No charge — the distinction means nothing
 * there. Time with no project and no client can only be Internal.
 */
export function kindChoices(project?: Pick<Project, "billingType"> | null, hasClient = false): EntryKind[] {
  if (isInternalProject(project)) return ["internal"];
  if (project) return isFlatFee(project) ? ["project", "internal"] : ["hourly", "no_charge", "internal"];
  return hasClient ? ["hourly", "no_charge", "internal"] : ["internal"];
}

/** What a new entry starts as, so the choice is right without touching it. */
export function defaultKind(project?: Pick<Project, "billingType" | "billableDefault"> | null, hasClient = false): EntryKind {
  if (project) {
    if (isInternalProject(project)) return "internal";
    if (isFlatFee(project)) return "project";
    return project.billableDefault === false ? "no_charge" : "hourly";
  }
  return hasClient ? "hourly" : "internal";
}

/** A previously chosen kind, kept if it is still on offer, else the default. */
export function fitKind(kind: EntryKind, project?: Pick<Project, "billingType" | "billableDefault"> | null, hasClient = false): EntryKind {
  return kindChoices(project, hasClient).includes(kind) ? kind : defaultKind(project, hasClient);
}

/**
 * What a kind writes onto an entry. Project work is stored billable — it is
 * the client's work — but with no rate, so nothing multiplies it into money.
 */
export function kindFields(kind: EntryKind, rate: number | null): { billable: boolean; internal: boolean; rate: number | null } {
  return {
    billable: kind === "hourly" || kind === "project",
    internal: kind === "internal",
    rate: kind === "hourly" ? rate : null,
  };
}

/** Billable time not yet on an invoice: the only kind that is ever owed a bill. */
export function isOwed(e: EntryShape & Pick<TimeEntry, "invoicedAt">, project?: Pick<Project, "billingType"> | null): boolean {
  return kindOf(e, project) === "hourly" && !e.invoicedAt;
}
